<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{ stats: { value: string; label: string }[] }>()

const root = ref<HTMLElement | null>(null)
const shown = ref<string[]>(props.stats.map((stat) => stat.value))
let observer: IntersectionObserver | null = null
let frame = 0

/** "30s" -> { target: 30, suffix: "s" }; values without a leading number keep as-is */
function splitValue(value: string) {
  const match = /^(\d+)(.*)$/.exec(value)
  return match ? { target: Number(match[1]), suffix: match[2] } : null
}

function settle() {
  shown.value = props.stats.map((stat) => stat.value)
}

function countUp() {
  const parsed = props.stats.map((stat) => splitValue(stat.value))
  const startedAt = performance.now()
  const duration = 1100

  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration)
    const eased = 1 - Math.pow(1 - progress, 3)
    shown.value = parsed.map((item, index) =>
      item ? `${Math.round(item.target * eased)}${item.suffix}` : props.stats[index].value,
    )
    if (progress < 1) frame = requestAnimationFrame(tick)
  }
  frame = requestAnimationFrame(tick)
}

onMounted(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduced || !('IntersectionObserver' in window) || !root.value) {
    settle()
    return
  }

  shown.value = props.stats.map((stat) => splitValue(stat.value)?.suffix ?? stat.value)
  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer?.disconnect()
      observer = null
      countUp()
    },
    { threshold: 0.4 },
  )
  observer.observe(root.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  cancelAnimationFrame(frame)
})

// Language switch replaces the values: show the new ones instead of half-counted numbers
watch(
  () => props.stats.map((stat) => `${stat.value}|${stat.label}`).join('~'),
  () => {
    cancelAnimationFrame(frame)
    settle()
  },
)
</script>

<template>
  <section class="relative overflow-hidden py-12 sm:py-16">
    <div class="mx-auto max-w-6xl px-5 sm:px-8">
      <div
        ref="root"
        v-reveal
        class="grid grid-cols-3 divide-x divide-gray-200/70 rounded-[24px] border border-gray-100 bg-panel/50 px-2 py-7 text-center sm:px-10 sm:py-12 dark:divide-gray-800 dark:border-gray-800 dark:bg-panel/30"
      >
        <div v-for="(stat, index) in stats" :key="stat.label" class="min-w-0 px-1.5 sm:px-0">
          <div class="text-2xl font-black tabular-nums tracking-tight text-primary sm:text-5xl">
            {{ shown[index] ?? stat.value }}
          </div>
          <div class="mt-1.5 text-[11px] leading-tight text-gray-500 sm:mt-2 sm:text-sm dark:text-gray-400">
            {{ stat.label }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
